import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"
import type { TRadioTrack } from "@/types"
import type { IAPIParams } from "@/utils"
import { radioAPI } from "@/utils"

const LIMIT = 100

function getNextPageParam(
  lastPage: { data: TRadioTrack[] },
  _allPages: unknown[],
  lastPageParam: number,
): number | undefined {
  if (lastPage.data.length < LIMIT) return undefined
  return lastPageParam + LIMIT
}

interface IUseRadioTracksParams {
  sort?: string
  seed?: string
  userId?: string
  teamIds?: string[]
  isHidden?: boolean
  isLiked?: boolean
  isUnplayed?: boolean
  refetch?: boolean
}

export function useRadioTracks({
  sort = "created",
  userId,
  teamIds = [],
  isHidden,
  isLiked,
  isUnplayed,
  refetch,
  seed,
}: IUseRadioTracksParams = {}) {
  const where: IAPIParams["where"] = {}
  if (isHidden !== undefined) {
    where.is_hidden = isHidden
  }
  if (userId) {
    where.user_id = userId
  }
  if (isLiked !== undefined) {
    where.is_liked = isLiked
  }
  if (isUnplayed !== undefined) {
    where.is_unplayed = isUnplayed
  }
  if (teamIds.length > 0) {
    where.user__team_id = { $in: teamIds }
  }

  return useInfiniteQuery({
    queryKey: ["/radio/tracks", "infinite", { sort, userId, teamIds, isHidden, isLiked, isUnplayed }],
    queryFn: ({ pageParam = 0 }) =>
      radioAPI.fetchMany({
        limit: LIMIT,
        offset: pageParam,
        sort: [sort],
        where,
        seed,
      }) as Promise<{ data: TRadioTrack[] }>,
    initialPageParam: 0,
    getNextPageParam,
    placeholderData: keepPreviousData,
    refetchOnMount: refetch,
    refetchOnWindowFocus: refetch,
  })
}
